// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Button,
  Card,
  Description,
  H3,
  InlineAlert,
  MdPadding,
  MdSpacing,
  RectLoader,
  SmSpacing,
} from '@alto/design-system';
import React, { type Ref, useContext, useEffect } from 'react';
import { type View } from 'react-native';
import { useAnalytics, useTrackNodeSeen } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { type Prescription } from '~shared/types';
import { useGetCouponSignUpConfig } from '../queries/queries';
import { CouponAttestationActionSheet } from './CouponAttestationActionSheet';

type Props = {
  couponID: number;
  prescription: Prescription;
};

export const CouponCard = ({ couponID, prescription }: Props) => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();
  const { couponConfig, isLoading, error } = useGetCouponSignUpConfig(couponID);

  useEffect(() => {
    trackEvent({
      event: EVENTS.COPAY_COUPON__CARD_RENDERED,
      params: {
        couponID,
      },
    });
  }, [couponID, trackEvent]);

  const nodeSeenRef = useTrackNodeSeen({
    onSeen: () => {
      trackEvent({
        event: EVENTS.COPAY_COUPON__CARD_SEEN,
        params: {
          couponID,
        },
      });
    },
  });

  const handleAddCoupon = () => {
    trackEvent({
      event: EVENTS.COPAY_COUPON__CARD_APPLY_COUPON_PRESSED,
      params: {
        couponID,
      },
    });

    setActiveActionSheet(
      <CouponAttestationActionSheet
        couponID={couponID}
        prescription={prescription}
      />,
    );
  };

  if (isLoading) {
    return (
      <Card style={{ backgroundColor: COLORS.BACKGROUND_COLORS.SECONDARY_LIGHTEST }}>
        <MdPadding>
          <RectLoader
            accessibilityLabel="discount offer loading"
            height={200}
          />
        </MdPadding>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ backgroundColor: COLORS.BACKGROUND_COLORS.SECONDARY_LIGHTEST }}>
        <MdPadding>
          <InlineAlert type="error">
            <Description>
              Unable to get discount information. Please message support if the problem persists.
            </Description>
          </InlineAlert>
        </MdPadding>
      </Card>
    );
  }

  return (
    <Card
      ref={nodeSeenRef as Ref<View>}
      style={{ backgroundColor: COLORS.BACKGROUND_COLORS.SECONDARY_LIGHTEST }}
    >
      <MdPadding>
        <H3>{couponConfig?.title}</H3>
        <SmSpacing />
        <Description>{couponConfig?.description}</Description>
        <MdSpacing />
        <Button
          label={couponConfig?.cta_text ?? 'Confirm eligibility'}
          type="secondary"
          small
          onPress={handleAddCoupon}
        />
      </MdPadding>
    </Card>
  );
};
